<script setup>
import { ref, computed, inject, watch } from 'vue'
import { useRequest } from 'composables/useRequest'
import { useConstants } from 'composables/useConstants'
import FormQuestions from './FormQuestions.vue'
import GuestEmails from 'controllers/vue_components/fields/guest_emails_public_event'

const emit = defineEmits(['back', 'slot-conflict'])

const { axiosRequest } = useRequest()
const { I18N } = useConstants()

const event          = inject('event')
const attendee       = inject('attendee')
const selectedSlot   = inject('selectedSlot')
const guestEmails    = inject('guestEmails')
const userUrl        = inject('userUrl')
const eventUrl       = inject('eventUrl')
const isEmbedded     = inject('isEmbedded')
const rescheduleData = inject('rescheduleData', null)

const isRescheduleMode = !!rescheduleData

const questionsRef    = ref(null)
const submitting      = ref(false)
const bookingError    = ref('')
const guestEmailsList = ref([...(guestEmails.value ?? [])])
const hasInvalidEmail = ref(false)
const inviteePhone    = ref('')
const inviteeLocation = ref('')

const nameError  = computed(() => attendee.value.full_name?.trim() ? '' : 'Name is required')
const emailError = computed(() => attendee.value.email?.trim() ? '' : 'Email is required')
const canSubmit  = computed(() =>
  !nameError.value && !emailError.value && !hasInvalidEmail.value && !submitting.value &&
  (!isPhoneCalling.value || inviteePhone.value.trim() !== '')
)

const questions      = computed(() => event.value?.active_event_questions_blueprint ?? [])
const showQuestions  = computed(() => questions.value.length > 0)

// ── Block state from conditional logic ───────────────────────────────────────
const isBlocked     = ref(false)
const blockMessage  = ref('')

watch(
  () => questionsRef.value?.blockState,
  (state) => {
    isBlocked.value    = state?.active ?? false
    blockMessage.value = state?.message ?? ''
  },
  { deep: true }
)

// ── Redirect state from conditional logic ─────────────────────────────────────
const isRedirecting   = ref(false)
let   _redirectTimer  = null

watch(
  () => questionsRef.value?.redirectState,
  (state) => {
    clearTimeout(_redirectTimer)
    if (state?.active && state.url) {
      isRedirecting.value = true
      _redirectTimer = setTimeout(() => {
        try {
          // Escape iframe for embedded booking forms
          const target = window.parent !== window ? window.parent : window
          target.location.href = state.url
        } catch {
          window.location.href = state.url
        }
      }, 1500)
    } else {
      isRedirecting.value = false
    }
  },
  { deep: true }
)
const isPhoneCalling = computed(() =>
  event.value?.location?.name === 'phone_call' &&
  event.value?.location?.phone_call?.selected_option === 'host_will_call_invitee'
)
const isAskInvitee = computed(() => event.value?.location?.name === 'ask_invitee')

function updateGuestEmails(emails, hasInvalid) {
  guestEmailsList.value = emails
  hasInvalidEmail.value = hasInvalid
}

async function submit() {
  if (!canSubmit.value) return
  submitting.value   = true
  bookingError.value = ''

  const bookingQuestionsAttributes = questionsRef.value?.getAnswers() ?? []

  const locationDetails = { ...(event.value?.location ?? {}) }
  if (isPhoneCalling.value) {
    locationDetails.phone_call = { ...locationDetails.phone_call, invitee_contact_number: inviteePhone.value }
  }
  if (isAskInvitee.value) {
    locationDetails.ask_invitee = { ...locationDetails.ask_invitee, location_given_by_attendee: inviteeLocation.value }
  }

  const payload = {
    attendee: attendee.value,
    booking_details: {
      selected_slot: selectedSlot.value,
      location_details: locationDetails,
      booking_questions_attributes: bookingQuestionsAttributes,
    },
    guest_emails: guestEmailsList.value,
  }

  let url = isRescheduleMode
    ? `/api/${userUrl}/${eventUrl}/bookings/${rescheduleData.booking_id}/submit_reschedule_booking_form`
    : `/api/${userUrl}/${eventUrl}/add_booking`
  if (!isRescheduleMode && isEmbedded) url += '?embed=true'

  const res = await axiosRequest('POST', url, payload, false)

  if (!res.js_error && res.response.success) {
    const { enable_external_payment, external_payment_page_url, id } = res.response.data
    if (!isRescheduleMode && enable_external_payment) {
      window.location.href = `${external_payment_page_url}?booking_id=${id}`
    } else {
      window.location.href = isEmbedded ? `/public/bookings/${id}/embed` : `/public/bookings/${id}`
    }
  } else {
    submitting.value = false
    const body = res.error?.response?.data ?? res.error?.data ?? res.response ?? {}
    bookingError.value = body.message ?? 'Something went wrong. Please try again.'

    // Someone took this slot between the calendar loading and this submit. The
    // error_code is set by the server so this does not depend on matching a
    // translated message.
    if (body.data?.error_code === 'slot_unavailable') emit('slot-conflict')
  }
}
</script>

<template>
  <div id="timelydo_public_event_booking_form" class="h-full overflow-y-auto scrollbar-style flex flex-col">

    <!-- ── Header with back button ─────────────────────────────────── -->
    <div class="flex items-center gap-3 px-5 pt-5 pb-4 flex-shrink-0
                border-b border-gray-100 dark:border-gray-700/60">
      <button @click="$emit('back')"
              type="button"
              class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700
                     transition-colors cursor-pointer flex-shrink-0"
              aria-label="Change time">
        <svg class="w-4 h-4 fill-gray-500 dark:fill-gray-400" viewBox="0 0 448 512">
          <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0
                   s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32
                   l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
        </svg>
      </button>
      <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
        Enter details
      </p>
    </div>

    <!-- ── Form ───────────────────────────────────────────────────── -->
    <div class="px-5 py-5 flex-1">
      <form @submit.prevent="submit" @keydown.enter.prevent="" class="space-y-5 max-w-sm">

        <!-- Full name -->
        <div id="timelydo_public_event_name_field">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.application?.events?.name ?? 'Name' }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="attendee.full_name"
                 type="text"
                 required
                 id="attendee_name"
                 :placeholder="I18N.application?.events?.name ?? 'Your full name'"
                 class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          <p v-if="nameError && attendee.full_name !== ''"
             class="mt-1.5 text-xs text-red-500">{{ nameError }}</p>
        </div>

        <!-- Email -->
        <div id="timelydo_public_event_email_field">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.application?.events?.email ?? 'Email' }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="attendee.email"
                 type="email"
                 required
                 id="attendee_email"
                 :placeholder="I18N.application?.events?.email ?? 'your@email.com'"
                 class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
          <p v-if="emailError && attendee.email !== ''"
             class="mt-1.5 text-xs text-red-500">{{ emailError }}</p>
        </div>

        <!-- Guest emails — not shown when rescheduling -->
        <div v-if="!isRescheduleMode && event?.enable_guest_emails" id="timelydo_public_event_guest_emails">
          <GuestEmails
            :limit="event.guest_emails_limit"
            :guest_emails="guestEmailsList"
            @guestEmailsChanged="updateGuestEmails"
          />
          <p v-if="hasInvalidEmail" class="mt-1 text-xs text-red-500">
            {{ I18N.application?.new_event?.invalid_email_added ?? 'Invalid email in guest list' }}
          </p>
        </div>

        <!-- Phone -->
        <div v-if="isPhoneCalling" id="timelydo_public_event_phone_field">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.application?.events?.phone_number ?? 'Phone number' }}
            <span class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="inviteePhone"
                 type="tel"
                 required
                 :placeholder="I18N.application?.events?.phone_number ?? '+1 555 000 0000'"
                 class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
        </div>

        <!-- Ask invitee for location -->
        <div v-if="isAskInvitee" id="timelydo_public_event_location_field">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ I18N.application?.events?.set_location ?? 'Location' }}
          </label>
          <textarea v-model="inviteeLocation"
                    rows="3"
                    :placeholder="I18N.application?.events?.set_location ?? 'Meeting location'"
                    class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all leading-relaxed" />
        </div>

        <!-- Form questions -->
        <div v-if="showQuestions" id="timelydo_public_event_form_questions">
          <FormQuestions ref="questionsRef" :questions="questions" />
        </div>

        <!-- Redirect banner — shown 1.5s before navigating away -->
        <Transition name="block-banner">
          <div v-if="isRedirecting"
               class="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/20">
            <svg class="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            <p class="text-sm text-orange-700 dark:text-orange-300">Based on your answers, we are redirecting you to the right page…</p>
          </div>
        </Transition>

        <!-- Block banner — shown when a condition action is 'block' -->
        <Transition name="block-banner">
          <div v-if="isBlocked"
               class="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20">
            <svg class="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
            </svg>
            <p class="text-sm text-red-700 dark:text-red-300 leading-relaxed">{{ blockMessage }}</p>
          </div>
        </Transition>

        <!-- Error -->
        <p v-if="bookingError" class="text-sm text-red-500">{{ bookingError }}</p>

        <!-- Submit -->
        <button id="timelydo_public_event_schedule_btn"
                type="submit"
                :disabled="!canSubmit || isBlocked || isRedirecting"
                :class="canSubmit && !isBlocked && !isRedirecting ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer' : 'bg-blue-400 cursor-not-allowed'"
                class="w-full rounded-[5px] px-4 py-3 text-sm font-medium text-white
                       transition-colors flex items-center justify-center gap-2">
          <svg v-if="submitting" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          {{ submitting
            ? (isRescheduleMode ? (I18N.website_content?.public?.rescheduling ?? 'Rescheduling...') : (I18N.website_content?.public?.scheduling ?? 'Scheduling...'))
            : (isRescheduleMode ? (I18N.website_content?.public?.reschedule_event ?? 'Reschedule Event') : (I18N.website_content?.public?.schedule_event ?? 'Schedule Event')) }}
        </button>

      </form>
    </div>

  </div>
</template>
